export const PREMIUM_L5_L7_PAGE_PATH =
  "/premium-level-5-level-7-fast-track-package/";

export const PREMIUM_L5_L7_LEVEL_5_PATH =
  "/courses/vtct/level-5-certificate-in-non-surgical-aesthetic-procedures-for-skin/";

export const PREMIUM_L5_L7_LEVEL_7_PATH =
  "/courses/vtct/level-7-diploma-in-non-surgical-aesthetic-injectable-procedures/";

/** Course pages where the fast-track package banner is shown. Hidden on the Level 5 and Level 7 courses. */
export const PREMIUM_L5_L7_SIGNPOST_PATHS: readonly string[] = [];

/** Hasura event_detail UUID for website checkout (normal onsite course + event). */
export const PREMIUM_L5_L7_EVENT_DETAIL_ID =
  "10d25132-18ba-454e-86d8-c52ba9da8355";

/** Sellable October 2026 cohort event. */
export const PREMIUM_L5_L7_EVENT_ID =
  "cc310e9c-fc28-4b72-8c0e-7e841492419a";

/** Charged price on event_detail.standard_price (pence, ex VAT). */
export const PREMIUM_L5_L7_PACKAGE_PRICE_EX_VAT_PENCE = 583333;
/** Customer-facing package total including VAT. */
export const PREMIUM_L5_L7_PACKAGE_PRICE_INC_VAT_GBP = 7000;
export const PREMIUM_L5_L7_VALUE_PRICE_GBP = 11000;
export const PREMIUM_L5_L7_PACKAGE_PRICE_GBP =
  PREMIUM_L5_L7_PACKAGE_PRICE_INC_VAT_GBP;
export const PREMIUM_L5_L7_PACKAGE_PRICE_RANGE_LABEL = "£7,000";

/** Catalogue standard prices (pence, ex VAT) for the included qualifications. */
export const PREMIUM_L5_L7_LEVEL_5_STANDARD_PRICE_EX_VAT_PENCE = 300000;
export const PREMIUM_L5_L7_LEVEL_7_STANDARD_PRICE_EX_VAT_PENCE = 583250;
export const PREMIUM_L5_L7_PLACES = 6;
export const PREMIUM_L5_L7_COHORT_LABEL = "October 2026 cohort";
export const PREMIUM_L5_L7_DAY_LABEL = "Fridays";
/** First teaching Friday (ISO date). */
export const PREMIUM_L5_L7_START_DATE = "2026-10-02";

export const PREMIUM_L5_L7_TUTORS = [
  "Charmelle",
  "Sara",
  "Charlotte",
] as const;

export const PREMIUM_L5_L7_TUTORS_LABEL = "Charmelle, Sara and Charlotte";

export const PREMIUM_L5_L7_TITLE =
  "Premium Level 5 & Level 7 Fast-Track Package";

export const PREMIUM_L5_L7_SEO_TITLE =
  "VTCT Level 5 and Level 7 Fast-Track London";

export const PREMIUM_L5_L7_SHORT_TITLE = "Level 5 & Level 7 Fast-Track";

export type PremiumL5L7SchedulePhase =
  | "level-5-formative"
  | "level-5-summative"
  | "level-5-theory"
  | "level-7-formative"
  | "level-7-theory"
  | "level-7-summative";

export type PremiumL5L7ScheduleSession = {
  week: number;
  date: string;
  label: string;
  phase: PremiumL5L7SchedulePhase;
  title: string;
  detail: string;
};

function addWeeks(isoDate: string, weeks: number) {
  const date = new Date(`${isoDate}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + weeks * 7);
  return date.toISOString().slice(0, 10);
}

/** Non-teaching Fridays skipped in the cohort calendar. */
const PREMIUM_L5_L7_HOLIDAY_FRIDAYS = new Set([
  "2026-12-25",
  "2027-01-01",
]);

function teachingFridaysFrom(startIso: string, count: number) {
  const dates: string[] = [];
  let week = 0;

  while (dates.length < count) {
    const date = addWeeks(startIso, week);
    week += 1;
    if (!PREMIUM_L5_L7_HOLIDAY_FRIDAYS.has(date)) {
      dates.push(date);
    }
  }

  return dates;
}

export function formatPremiumL5L7Date(isoDate: string) {
  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${isoDate}T12:00:00Z`));
}

/**
 * Weekly Fridays from 2 October 2026 (skips Christmas and New Year Fridays).
 * Level 5: 4 formative, 2 summative, theory exams.
 * Level 7: 4 formative, theory exams, 1 summative.
 */
const PREMIUM_L5_L7_SCHEDULE_BLUEPRINT: Array<
  Omit<PremiumL5L7ScheduleSession, "week" | "date" | "label">
> = [
  {
    phase: "level-5-formative",
    title: "Level 5 formative day 1",
    detail: "Formative practical training for Level 5 skin rejuvenation.",
  },
  {
    phase: "level-5-formative",
    title: "Level 5 formative day 2",
    detail: "Continue Level 5 formative practical training.",
  },
  {
    phase: "level-5-formative",
    title: "Level 5 formative day 3",
    detail: "Continue Level 5 formative practical training.",
  },
  {
    phase: "level-5-formative",
    title: "Level 5 formative day 4",
    detail: "Final Level 5 formative practical training day.",
  },
  {
    phase: "level-5-summative",
    title: "Level 5 summative day 1",
    detail: "Summative practical assessment for Level 5.",
  },
  {
    phase: "level-5-summative",
    title: "Level 5 summative day 2",
    detail: "Second Level 5 summative practical assessment day.",
  },
  {
    phase: "level-5-theory",
    title: "Level 5 theory exams",
    detail: "Level 5 theory examinations.",
  },
  {
    phase: "level-7-formative",
    title: "Level 7 formative day 1",
    detail: "Formative practical training for Level 7 injectables.",
  },
  {
    phase: "level-7-formative",
    title: "Level 7 formative day 2",
    detail: "Continue Level 7 formative practical training.",
  },
  {
    phase: "level-7-formative",
    title: "Level 7 formative day 3",
    detail: "Continue Level 7 formative practical training.",
  },
  {
    phase: "level-7-formative",
    title: "Level 7 formative day 4",
    detail: "Final Level 7 formative practical training day.",
  },
  {
    phase: "level-7-theory",
    title: "Level 7 theory exams",
    detail: "Level 7 theory examinations.",
  },
  {
    phase: "level-7-summative",
    title: "Level 7 summative day",
    detail: "Level 7 summative practical assessment.",
  },
];

const scheduleDates = teachingFridaysFrom(
  PREMIUM_L5_L7_START_DATE,
  PREMIUM_L5_L7_SCHEDULE_BLUEPRINT.length,
);

export const PREMIUM_L5_L7_SCHEDULE: PremiumL5L7ScheduleSession[] =
  PREMIUM_L5_L7_SCHEDULE_BLUEPRINT.map((session, index) => ({
    ...session,
    week: index + 1,
    date: scheduleDates[index],
    label: `Week ${index + 1}`,
  }));

export function getPremiumL5L7SessionsByPhase(
  phase: PremiumL5L7SchedulePhase | PremiumL5L7SchedulePhase[],
) {
  const phases = Array.isArray(phase) ? phase : [phase];
  return PREMIUM_L5_L7_SCHEDULE.filter((session) =>
    phases.includes(session.phase),
  );
}

export const PREMIUM_L5_L7_LEVEL_5_START =
  PREMIUM_L5_L7_SCHEDULE[0]?.date ?? PREMIUM_L5_L7_START_DATE;
export const PREMIUM_L5_L7_LEVEL_5_END =
  getPremiumL5L7SessionsByPhase("level-5-theory")[0]?.date ??
  PREMIUM_L5_L7_LEVEL_5_START;
export const PREMIUM_L5_L7_LEVEL_7_START =
  getPremiumL5L7SessionsByPhase("level-7-formative")[0]?.date ??
  PREMIUM_L5_L7_LEVEL_5_END;
export const PREMIUM_L5_L7_LEVEL_7_END =
  getPremiumL5L7SessionsByPhase("level-7-summative")[0]?.date ??
  PREMIUM_L5_L7_LEVEL_7_START;

export const PREMIUM_L5_L7_HERO_IMAGE =
  "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/4XMIFbp9EgevoLdtvEfwTF/mesotherapy.jpg";

export const PREMIUM_L5_L7_LEVEL_5_VIDEO =
  "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/578UK4dZu8dISUdGW1tAnn/Mesotherapy_Reel_-_Sequence_1-720p.mp4";

export const PREMIUM_L5_L7_LEVEL_7_VIDEO =
  "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/7lC5OkDixgTYelwolZ6Z1u/Level_7_Reel_-_Sequence_1-720p.mp4";

export const PREMIUM_L5_L7_LEVEL_5_IMAGE =
  "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/3u1dXBGdF3eXmJk7LYjRD3/shutterstock_2140727647-2.jpg";

export const PREMIUM_L5_L7_LEVEL_7_IMAGE =
  "https://s3.eu-west-2.amazonaws.com/assets.cosmetic.college/cms/CJiCvB4ASA77YdnbUAP42/Youtube_Landscape.00_01_01_01.Still005__1_.jpg";

export function normalizePagePath(path: string) {
  const base = path.split("#")[0].split("?")[0];
  return base.endsWith("/") ? base : `${base}/`;
}

export function isPremiumL5L7SignpostPath(path: string) {
  return PREMIUM_L5_L7_SIGNPOST_PATHS.includes(normalizePagePath(path));
}

export function buildPremiumL5L7CallbackPath(
  source = "premium_l5_l7_fast_track",
) {
  const params = new URLSearchParams({
    source,
    listing_category: "premium_l5_l7_fast_track",
    listing_url: PREMIUM_L5_L7_PAGE_PATH,
  });

  return `/request-a-callback/?${params.toString()}`;
}

import { addVatPence } from "@/utils/payment-plan";

export function formatGbp(amount: number) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(amount);
}

/** Format an ex-VAT pence amount as pounds including VAT. */
export function formatGbpIncVatFromExVatPence(exVatPence: number) {
  return formatGbp(Math.round(addVatPence(exVatPence) / 100));
}
